import { useMemo } from "react";

import type { ArrayKeys, ElementOf } from "./filter-params";
import { type FilterParamsHandle } from "./use-filter-params";
import { useUrlSyncedFilterParams } from "./use-url-synced-filter-params";
import {
  type ResultsTableQueryParams,
  ResultsTableSortableColumns,
} from "@/components/table/views/job/job-columns";
import { SortOrder, type FilterableCompetencyFits } from "@/types/api";
import type { Nullable } from "@/types/utils";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DynamicParams = Record<string, any>;

export interface ResultsFilterParamsHandle extends FilterParamsHandle<
  ResultsTableQueryParams & DynamicParams
> {
  getCompetency: (name: string) => Nullable<FilterableCompetencyFits>;
  setCompetency: (
    name: string,
    value: FilterableCompetencyFits,
  ) => ResultsFilterParamsHandle;
  deleteCompetency: (name: string) => ResultsFilterParamsHandle;
  hasAnyCompetencyFilters: () => boolean;
  getShowInSummary: (name: string) => Nullable<string[]>;
  setShowInSummary: (
    name: string,
    values: string[],
  ) => ResultsFilterParamsHandle;
  toggleShowInSummaryValue: (
    name: string,
    value: string,
  ) => ResultsFilterParamsHandle;
  hasShowInSummaryValue: (name: string, value: string) => boolean;
  deleteShowInSummary: (name: string) => ResultsFilterParamsHandle;
  hasAnyShowInSummaryFilters: () => boolean;
  hasAnyDynamicFilters: () => boolean;
}

interface UseResultsFilterParamsResult {
  handle: ResultsFilterParamsHandle;
  initialized: boolean;
}

const DEFAULT_RESULTS_PARAMS: Partial<ResultsTableQueryParams> = {
  sortBy: ResultsTableSortableColumns.CreatedAt,
  sortOrder: SortOrder.Desc,
};

type CombinedParams = ResultsTableQueryParams & DynamicParams;

export const useResultsFilterParams = (
  storageKeyPrefix: string,
  defaults: Partial<ResultsTableQueryParams> = DEFAULT_RESULTS_PARAMS,
): UseResultsFilterParamsResult => {
  const { handle: baseHandle, initialized } =
    useUrlSyncedFilterParams<CombinedParams>(storageKeyPrefix, defaults);

  const extendedHandle: ResultsFilterParamsHandle = useMemo(() => {
    const handle: ResultsFilterParamsHandle = {
      ...baseHandle,

      getCompetency: (name: string) =>
        baseHandle.get(
          `competencies.${name}`,
        ) as FilterableCompetencyFits | null,

      setCompetency: (name: string, value: FilterableCompetencyFits) => {
        baseHandle.set(`competencies.${name}`, value);
        return handle;
      },

      deleteCompetency: (name: string) => {
        baseHandle.delete(`competencies.${name}`);
        return handle;
      },

      hasAnyCompetencyFilters: () =>
        Array.from(baseHandle.keys()).some((k) =>
          String(k).startsWith("competencies."),
        ),

      getShowInSummary: (name: string) =>
        baseHandle.get(`showInSummary.${name}`) as Nullable<string[]>,

      setShowInSummary: (name: string, values: string[]) => {
        baseHandle.set(`showInSummary.${name}`, values);
        return handle;
      },

      toggleShowInSummaryValue: (name: string, value: string) => {
        baseHandle.toggleValue(
          `showInSummary.${name}` as ArrayKeys<CombinedParams>,
          value as ElementOf<CombinedParams[ArrayKeys<CombinedParams>]>,
        );
        return handle;
      },

      hasShowInSummaryValue: (name: string, value: string) =>
        baseHandle.has(
          `showInSummary.${name}` as ArrayKeys<CombinedParams>,
          value as ElementOf<CombinedParams[ArrayKeys<CombinedParams>]>,
        ),

      deleteShowInSummary: (name: string) => {
        baseHandle.delete(`showInSummary.${name}`);
        return handle;
      },

      hasAnyShowInSummaryFilters: () =>
        Array.from(baseHandle.keys()).some((k) =>
          String(k).startsWith("showInSummary."),
        ),

      hasAnyDynamicFilters: () =>
        Array.from(baseHandle.keys()).some(
          (k) =>
            String(k).startsWith("competencies.") ||
            String(k).startsWith("showInSummary."),
        ),
    };

    return handle;
  }, [baseHandle]);

  return { handle: extendedHandle, initialized };
};
